// Notification preferences slide-over. Reads / patches
// planner_notification_preference for the current user. Each toggle
// flips a boolean column on the server; the digest mode and quiet
// hours rows live alongside. The in-app bell always fires regardless
// of these settings — the toggles only gate email / Teams pushes
// (Drillcut behaviour preserved).

import { useEffect, useState } from "react";
import { Sheet } from "../components/Sheet";
import { tpApi } from "../api";
import { DC } from "./createSlideOver/primitives";

interface Prefs {
  notifyOnAssignment: boolean;
  notifyOnMention: boolean;
  notifyOnStatusChange: boolean;
  notifyOnMilestoneComplete: boolean;
  notifyOnIssueRaisedInMyTeam: boolean;
  notifyOnDueSoon: boolean;
  notifyOnOverdue: boolean;
  notifyViaEmail: boolean;
  notifyViaTeams: boolean;
  notifyForOwnChanges: boolean;
  digestMode: "immediate" | "daily" | "weekly";
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  timezone: string | null;
}

const TOGGLES: Array<{ key: keyof Prefs; label: string; hint: string }> = [
  { key: "notifyOnAssignment", label: "I'm assigned an item", hint: "Someone makes me the primary owner." },
  { key: "notifyOnMention", label: "I'm @mentioned", hint: "Comments that tag me directly." },
  { key: "notifyOnStatusChange", label: "Status changes on my items", hint: "On track ↔ off track, done, archived." },
  { key: "notifyOnMilestoneComplete", label: "A milestone completes", hint: "Sub-goal of any Rock I own / watch." },
  { key: "notifyOnIssueRaisedInMyTeam", label: "An opportunity is raised in my team", hint: "Team-scoped — opportunities with no home team aren't pushed." },
  { key: "notifyOnDueSoon", label: "Due-soon reminders", hint: "1-2 days before the due date." },
  { key: "notifyOnOverdue", label: "Overdue alerts", hint: "The day after an item passes its due date." },
];

export function NotificationPreferencesPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    void tpApi
      .getPreferences()
      .then((p) => setPrefs(p))
      .finally(() => setLoading(false));
  }, [open]);

  async function patch<K extends keyof Prefs>(key: K, value: Prefs[K]) {
    if (!prefs) return;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaving(true);
    try {
      await tpApi.updatePreferences({ [key]: value });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Notification preferences"
      footer={
        <span style={{ fontSize: 11, color: DC.grey, flex: 1 }}>
          {saving ? "Saving…" : "Changes save automatically. The in-app bell always fires."}
        </span>
      }
    >
      {loading || !prefs ? (
        <p style={{ color: DC.grey }}>Loading…</p>
      ) : (
        <>
          <section>
            <SectionLabel>Notify me when…</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {TOGGLES.map((t) => (
                <Toggle
                  key={String(t.key)}
                  label={t.label}
                  hint={t.hint}
                  checked={prefs[t.key] as boolean}
                  onChange={(v) => patch(t.key, v as Prefs[typeof t.key])}
                />
              ))}
            </div>
          </section>

          <section>
            <SectionLabel>Channels</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Toggle
                label="Email"
                hint="Delivered to your Microsoft 365 inbox."
                checked={prefs.notifyViaEmail}
                onChange={(v) => patch("notifyViaEmail", v)}
              />
              <Toggle
                label="Microsoft Teams"
                hint="Chat-card via the BSP Team Planner Teams app (when installed)."
                checked={prefs.notifyViaTeams}
                onChange={(v) => patch("notifyViaTeams", v)}
              />
              <Toggle
                label="Send me notifications for my own changes"
                hint="Off by default. Turn on to also receive cards / emails for the rocks, to-dos, milestones, and comments you create or update yourself — useful for self-testing or wanting an audit trail."
                checked={prefs.notifyForOwnChanges}
                onChange={(v) => patch("notifyForOwnChanges", v)}
              />
            </div>
          </section>

          <section>
            <SectionLabel>Digest</SectionLabel>
            <div style={{ display: "flex", gap: 6 }}>
              {(["immediate", "daily", "weekly"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => patch("digestMode", mode)}
                  style={{
                    flex: 1,
                    height: 36,
                    padding: "0 10px",
                    border: `1px solid ${prefs.digestMode === mode ? DC.primary : DC.borderStrong}`,
                    background: prefs.digestMode === mode ? DC.primaryTint : DC.surface,
                    color: prefs.digestMode === mode ? DC.primary : DC.greyDeep,
                    borderRadius: 6,
                    font: "700 11px/1 var(--bsp-font-sans)",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  {mode}
                </button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: DC.grey, marginTop: 8 }}>
              Immediate → fire as events happen. Daily → bundle into one summary
              at 8am local. Weekly → Monday rollup.
            </p>
          </section>

          <section>
            <SectionLabel>Quiet hours</SectionLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <FieldLabel>Start</FieldLabel>
                <input
                  type="time"
                  className="bsp-input"
                  value={prefs.quietHoursStart ?? ""}
                  onChange={(e) =>
                    patch("quietHoursStart", e.target.value || null)
                  }
                />
              </div>
              <div>
                <FieldLabel>End</FieldLabel>
                <input
                  type="time"
                  className="bsp-input"
                  value={prefs.quietHoursEnd ?? ""}
                  onChange={(e) =>
                    patch("quietHoursEnd", e.target.value || null)
                  }
                />
              </div>
            </div>
            <p style={{ fontSize: 11, color: DC.grey, marginTop: 8 }}>
              During quiet hours, notifications buffer and deliver when the
              window ends. Leave both blank to disable.
            </p>
          </section>

          <section>
            <SectionLabel>Timezone</SectionLabel>
            <input
              className="bsp-input"
              placeholder="Australia/Sydney"
              value={prefs.timezone ?? ""}
              onBlur={(e) => patch("timezone", e.target.value.trim() || null)}
              onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}
            />
            <p style={{ fontSize: 11, color: DC.grey, marginTop: 6 }}>
              IANA timezone — anchors quiet-hour boundaries and digest delivery
              times. Falls back to the org default when blank.
            </p>
          </section>
        </>
      )}
    </Sheet>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        font: "700 10px/1 var(--bsp-font-sans)",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: DC.grey,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        font: "700 10px/1 var(--bsp-font-sans)",
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        color: DC.grey,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 12px",
        background: DC.surface,
        border: `1px solid ${DC.border}`,
        borderRadius: 6,
        cursor: "pointer",
      }}
    >
      <span
        role="switch"
        aria-checked={checked}
        onClick={(e) => {
          e.preventDefault();
          onChange(!checked);
        }}
        style={{
          width: 32,
          height: 18,
          borderRadius: 999,
          background: checked ? DC.primary : "var(--ink-300)",
          position: "relative",
          flexShrink: 0,
          marginTop: 1,
          transition: "background 120ms",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: checked ? 16 : 2,
            width: 14,
            height: 14,
            borderRadius: 999,
            background: "#fff",
            transition: "left 120ms",
            boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
          }}
        />
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        {hint ? (
          <div style={{ fontSize: 11, color: DC.grey, marginTop: 2 }}>{hint}</div>
        ) : null}
      </div>
    </label>
  );
}
